<script lang="ts">
export default {
  name: "SearchResultsItem",
};
</script>

<script setup lang="ts">
import SearchResultsItemInner from "@/components/search/ResultsItem/Inner.vue";
import type { SearchResult } from "@/services/fobizzPlatformApi";
import { Item, Material, Folder } from "@/services/fobizzToolsApi";

type Props = {
  result: Item | Material | Folder | SearchResult;
  gallery?: boolean;
  noLink?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  gallery: false,
  noLink: false,
});

const emit = defineEmits(["select"]);

const courseUrl = computed(
  () =>
    "kind" in props.result &&
    (props.result.kind === "course" ||
      props.result.kind === "webinar" ||
      props.result.kind === "impulse") &&
    "url" in props.result &&
    props.result.url
);

const toOrHref = computed(() => {
  if (props.noLink) {
    return {};
  } else if (props.gallery && "shareUrl" in props.result) {
    return { href: props.result.shareUrl };
  } else if ("clientPath" in props.result) {
    return {
      to: props.result.clientPath,
    };
  } else if (courseUrl.value) {
    return { href: courseUrl.value };
  } else {
    return {
      to: { name: "folders-show", params: { id: props.result.id } },
    };
  }
});

const component = computed(() => {
  if (props.noLink) {
    return "div";
  } else if (props.gallery || courseUrl.value) {
    return "a";
  } else {
    return "router-link";
  }
});
</script>

<template>
  <li class="result-item">
    <button
      v-if="noLink"
      class="result-item__link"
      type="button"
      @click.once="emit('select')"
    >
      <SearchResultsItemInner :result="result" :gallery="gallery" />
    </button>
    <component
      :is="component"
      v-else
      class="result-item__link"
      v-bind="toOrHref"
      role="option"
    >
      <SearchResultsItemInner :result="result" :gallery="gallery" />
    </component>
  </li>
</template>

<style lang="scss" scoped>
.result-item {
  display: flex;
  align-items: center;

  &__link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 1;
    gap: $spacer-3;
    padding: $spacer-2 $spacer-3;
    text-decoration: none;
    transition: background-color 0.2s ease-in-out;
    border-left: 3px solid transparent;
    font-size: $font-size-sm;
    line-height: 1.4;
    border: none;
    background: none;

    &:hover {
      background-color: $hover-color;
    }

    &[aria-selected="true"] {
      background-color: $hover-color;
      border-left: 3px solid $color-fobi;
    }
  }
}
</style>
